import { Alert, Card, Empty, Skeleton, Space, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useFetch } from "../../hooks/useFetch";
import { PostagemVendaPendentePagamento } from "../../interfaces/PostagemVendaPendentePagamento";
import { buscarPostagensVendaPendentesPagamentoService } from "../../services/buscarPostagensVendaPendentesPagamentoService";
import { formatDate } from "../../uteis/dateUtils";
import {
  Container,
  Description,
  Header,
  Title,
} from "../ViewDashboardOperacional/styles";

function isSemAssinatura(assinatura?: string) {
  if (!assinatura) {
    return false;
  }

  return (
    assinatura
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase() === "nao"
  );
}

function getAssinaturaLabel(assinatura?: string) {
  if (isSemAssinatura(assinatura)) {
    return "Inexistente";
  }

  return "Pendente pagamento";
}

export default function ViewDashboardOperacionalVendaSemAssinatura() {
  const {
    data: postagens,
    isLoading,
    error,
  } = useFetch(true, buscarPostagensVendaPendentesPagamentoService, []);

  const pessoaFilters = postagens?.map((item) => item.nome).filter(Boolean) ?? [];
  const empreendimentoFilters =
    postagens?.map((item) => item.empreendimento).filter(Boolean) ?? [];

  const columns: ColumnsType<PostagemVendaPendentePagamento> = [
    {
      title: "Pessoa",
      dataIndex: "nome",
      key: "nome",
      width: 260,
      filters: Array.from(new Set(pessoaFilters))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          text: value,
          value,
        })),
      filterSearch: true,
      onFilter: (value, record) => record.nome === value,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.nome}</span>
          {record.telefone ? (
            <Typography.Text type="secondary">
              {record.telefone}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Empreendimento",
      dataIndex: "empreendimento",
      key: "empreendimento",
      width: 260,
      filters: Array.from(new Set(empreendimentoFilters))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          text: value,
          value,
        })),
      filterSearch: true,
      onFilter: (value, record) => record.empreendimento === value,
    },
    {
      title: "Assinatura",
      dataIndex: "assinatura",
      key: "assinatura",
      width: 240,
      filters: [
        {
          text: "Inexistente",
          value: "nao",
        },
        {
          text: "Pendente pagamento",
          value: "pendente",
        },
      ],
      onFilter: (value, record) => {
        if (value === "nao") {
          return isSemAssinatura(record.assinatura);
        }

        return !isSemAssinatura(record.assinatura);
      },
      render: (value: string) => getAssinaturaLabel(value),
    },
    {
      title: "Data da postagem",
      dataIndex: "dataDivulgacao",
      key: "dataDivulgacao",
      width: 180,
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        new Date(a.dataDivulgacao).getTime() - new Date(b.dataDivulgacao).getTime(),
      render: (value: string) => formatDate(value),
    },
  ];

  return (
    <Container>
      <Header>
        <Title>Postagens de venda pendentes de pagamento</Title>
        <Description>
          Lista das postagens de venda com assinatura inexistente ou pendente
          de pagamento.
        </Description>
      </Header>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Nao foi possivel carregar as postagens de venda sem assinatura."
          description="Verifique a conexao com a API e tente novamente."
        />
      ) : !postagens ? (
        <Alert
          type="warning"
          showIcon
          message="Nenhuma resposta foi retornada para as postagens de venda."
        />
      ) : postagens.length === 0 ? (
        <Card>
          <Empty description="Nenhuma postagem de venda pendente de pagamento encontrada." />
        </Card>
      ) : (
        <Card
          title={`Total de postagens pendentes de pagamento: ${postagens.length}`}
          bodyStyle={{ padding: 0 }}
        >
          <Table<PostagemVendaPendentePagamento>
            rowKey={(record, index) =>
              `${record.nome}-${record.empreendimento}-${record.dataDivulgacao}-${index}`
            }
            columns={columns}
            dataSource={postagens}
            scroll={{ x: 760 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
          />
        </Card>
      )}
    </Container>
  );
}
